/**
 * Output Footer Sections — sanitize the authored HTML once, and convert it into
 * a small block model the PDF generator can draw.
 *
 * The results page injects `sanitizeSectionHtml()` output directly; the PDF
 * (hand-drawn jsPDF, no HTML renderer) walks `htmlToBlocks()`. Both start from
 * the same DOMPurify pass so screen and PDF can never disagree on what was
 * stripped. The server has already reduced the HTML to a semantic whitelist —
 * this is the second line, not the first.
 *
 * Block model: headings, paragraphs of inline runs (bold / italic / link),
 * lists, images (a <figure> with caption, or an <img> alone in a paragraph —
 * which is what the editor's image button produces), tables, rules and
 * spacers. Consecutive images are grouped into `imageRow`s of up to four; an
 * explicit <div class="os-row"> forces exactly its images onto one row.
 */

import DOMPurify from 'dompurify';

export interface InlineRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  href?: string;
  /** An explicit line break (text is ''). */
  br?: true;
}

export interface ImageBlock {
  type: 'image';
  src: string;
  alt: string;
  caption?: InlineRun[];
  /** Fraction of the column width the author sized the image to (0.2..1). */
  widthHint: number;
}

/** A header cell naming a product colour is drawn AS that colour (the worksheet's WHITE / GRAY groups). */
export type ColourSwatch = 'white' | 'gray';

const SWATCH_WORDS: Record<string, ColourSwatch> = {
  WHITE: 'white', BLANC: 'white', BLANCHE: 'white', BLANCO: 'white', BLANCA: 'white',
  GRAY: 'gray', GREY: 'gray', GRIS: 'gray',
};

/** The swatch a header cell's text names, if it is exactly a colour word (any of the shipped languages). */
export function colourSwatchOf(text: string): ColourSwatch | undefined {
  return SWATCH_WORDS[text.replace(/ /g, ' ').trim().toUpperCase()];
}

export interface TableCell {
  runs: InlineRun[];
  header: boolean;
  colSpan: number;
  rowSpan: number;
  /** Set on a header cell whose text is a product colour name — see colourSwatchOf(). */
  swatch?: ColourSwatch;
  /**
   * A placeholder occupying a grid slot that a cell in an EARLIER row spans
   * into (rowspan). Never authored: inserted by tableBlock() so every row's
   * cells line up column-for-column. Renderers draw nothing for it.
   */
  spanned?: true;
}

export type SectionBlock =
  | { type: 'heading'; level: 2 | 3 | 4; runs: InlineRun[] }
  | { type: 'paragraph'; runs: InlineRun[]; align: 'left' | 'center' | 'right' }
  | { type: 'list'; ordered: boolean; items: { runs: InlineRun[]; depth: number }[] }
  | ImageBlock
  | { type: 'imageRow'; images: ImageBlock[] }
  | { type: 'table'; hasHeaderRow: boolean; rows: { cells: TableCell[] }[] }
  | { type: 'hr' }
  | { type: 'spacer'; height: number };

export const MAX_IMAGES_PER_ROW = 4;

/** Editor-sized images: `width="450"` on a ~900 px editing column ≈ half width. */
const EDITOR_COLUMN_PX = 900;

/**
 * DOMPurify pass + link hardening. Returns HTML ready for dangerouslySetInnerHTML.
 * Image width/height attributes are dropped so the responsive CSS wins on screen
 * (the PDF walker reads them from the DOM before this serialises).
 */
export function sanitizeSectionHtml(html: string): string {
  const doc = parseSection(html);
  if (!doc) return '';
  doc.body.querySelectorAll('img').forEach((img) => { img.removeAttribute('width'); img.removeAttribute('height'); });
  // Colour-group header cells get a hook the CSS can colour (no classes survive the sanitizer).
  doc.body.querySelectorAll('th').forEach((th) => {
    const swatch = colourSwatchOf(textOf(th));
    if (swatch) th.setAttribute('data-swatch', swatch);
  });
  return doc.body.innerHTML;
}

/** Sanitized DOM for the section, with links hardened. Null outside a browser. */
function parseSection(html: string): Document | null {
  if (typeof DOMParser === 'undefined') return null;
  const clean = DOMPurify.sanitize(html ?? '', { ADD_ATTR: ['target'], FORBID_TAGS: ['style'], FORBID_ATTR: ['style'] });
  const doc = new DOMParser().parseFromString(`<!doctype html><body>${clean}`, 'text/html');
  doc.body.querySelectorAll('a[href]').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  groupFigures(doc);
  return doc;
}

/**
 * Normalise images into <figure>s and wrap consecutive figures (nothing but
 * whitespace between them) in <div class="os-row"> in chunks of up to
 * MAX_IMAGES_PER_ROW. This is what puts "two pictures side by side, captions
 * underneath" on screen and in print — CSS alone cannot group siblings — and
 * it mirrors the row grouping the PDF walker applies, so all three outputs
 * agree on the layout. Author-written .os-row containers are left as they are.
 */
function groupFigures(doc: Document): void {
  const body = doc.body;

  // 1. A text-free <p> holding images becomes one <figure> per image; a bare
  //    <img> at block level is wrapped in a <figure>.
  Array.from(body.children).forEach((el) => {
    if (el.tagName === 'P' && el.querySelector('img') && textOf(el).trim() === '') {
      const figs = Array.from(el.querySelectorAll('img')).map((img) => {
        const f = doc.createElement('figure');
        f.appendChild(img);
        return f;
      });
      figs.forEach((f) => body.insertBefore(f, el));
      body.removeChild(el);
    } else if (el.tagName === 'IMG') {
      const f = doc.createElement('figure');
      body.insertBefore(f, el);
      f.appendChild(el);
    }
  });

  // 2. Wrap runs of adjacent figures.
  const nodes = Array.from(body.childNodes);
  let run: Element[] = [];
  let gaps: Node[] = []; // whitespace text nodes sitting between the run's figures
  const flush = () => {
    if (run.length >= 2) {
      gaps.forEach((g) => g.parentNode?.removeChild(g));
      for (let i = 0; i < run.length; i += MAX_IMAGES_PER_ROW) {
        const chunk = run.slice(i, i + MAX_IMAGES_PER_ROW);
        if (chunk.length < 2) break;
        const row = doc.createElement('div');
        row.className = 'os-row';
        body.insertBefore(row, chunk[0]);
        chunk.forEach((f) => row.appendChild(f));
      }
    }
    run = []; gaps = [];
  };
  nodes.forEach((n) => {
    if (n.nodeType === Node.ELEMENT_NODE && (n as Element).tagName === 'FIGURE' && (n as Element).querySelector('img')) {
      run.push(n as Element);
    } else if (n.nodeType === Node.TEXT_NODE && (n.textContent ?? '').trim() === '') {
      if (run.length) gaps.push(n); // whitespace between figures does not break a run
    } else {
      flush();
    }
  });
  flush();
}

export function htmlToBlocks(html: string): SectionBlock[] {
  const doc = parseSection(html);
  if (!doc) return [];
  const out: SectionBlock[] = [];
  walkBlockChildren(doc.body, out);
  return groupImages(out.filter(isMeaningful));
}

// ── Block walk ───────────────────────────────────────────────────────────────

const BLOCK_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'UL', 'OL', 'TABLE', 'HR', 'FIGURE', 'DIV', 'SECTION', 'ARTICLE', 'BLOCKQUOTE', 'BR', 'IMG', 'LI', 'PRE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE', 'NAV']);

function walkBlockChildren(parent: Element, out: SectionBlock[]): void {
  // Loose inline content directly under a block container (e.g. text typed
  // outside any <p>) is gathered into implicit paragraphs.
  let pending: Node[] = [];
  const flush = () => {
    if (pending.length) {
      const runs = collectRuns(pending);
      if (runs.some((r) => r.text.trim() !== '')) out.push({ type: 'paragraph', runs, align: 'left' });
      pending = [];
    }
  };

  parent.childNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName)) {
      flush();
      walkBlock(node as Element, out);
    } else if (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE) {
      pending.push(node);
    }
  });
  flush();
}

function walkBlock(el: Element, out: SectionBlock[]): void {
  switch (el.tagName) {
    case 'H1': case 'H2':
      out.push({ type: 'heading', level: 2, runs: collectRuns(el.childNodes) }); return;
    case 'H3':
      out.push({ type: 'heading', level: 3, runs: collectRuns(el.childNodes) }); return;
    case 'H4': case 'H5': case 'H6':
      out.push({ type: 'heading', level: 4, runs: collectRuns(el.childNodes) }); return;
    case 'P': case 'BLOCKQUOTE': case 'PRE': {
      const imgs = Array.from(el.querySelectorAll('img'));
      if (imgs.length && textOf(el).trim() === '') {
        imgs.forEach((img) => out.push(imageBlock(img, undefined)));
        return;
      }
      out.push({ type: 'paragraph', runs: collectRuns(el.childNodes), align: alignOf(el) });
      return;
    }
    case 'UL': case 'OL': {
      const items: { runs: InlineRun[]; depth: number }[] = [];
      collectListItems(el, 0, items);
      if (items.length) out.push({ type: 'list', ordered: el.tagName === 'OL', items });
      return;
    }
    case 'TABLE': {
      const t = tableBlock(el);
      if (t.rows.length) out.push(t);
      return;
    }
    case 'HR': out.push({ type: 'hr' }); return;
    case 'BR': out.push({ type: 'spacer', height: 2 }); return;
    case 'IMG': out.push(imageBlock(el as HTMLImageElement, undefined)); return;
    case 'FIGURE': {
      const img = el.querySelector('img');
      if (!img) { walkBlockChildren(el, out); return; }
      const cap = el.querySelector('figcaption');
      out.push(imageBlock(img, cap ? collectRuns(cap.childNodes) : undefined));
      return;
    }
    case 'DIV': {
      if (el.classList.contains('os-row')) {
        const imgs: ImageBlock[] = [];
        const inner: SectionBlock[] = [];
        walkBlockChildren(el, inner);
        inner.forEach((b) => { if (b.type === 'image') imgs.push(b); });
        for (let i = 0; i < imgs.length; i += MAX_IMAGES_PER_ROW) {
          const chunk = imgs.slice(i, i + MAX_IMAGES_PER_ROW);
          out.push(chunk.length === 1 ? chunk[0] : { type: 'imageRow', images: chunk });
        }
        return;
      }
      walkBlockChildren(el, out);
      return;
    }
    case 'LI': // stray <li> outside a list
      out.push({ type: 'paragraph', runs: collectRuns(el.childNodes), align: 'left' });
      return;
    default:
      walkBlockChildren(el, out);
  }
}

function collectListItems(list: Element, depth: number, items: { runs: InlineRun[]; depth: number }[]): void {
  Array.from(list.children).forEach((li) => {
    if (li.tagName !== 'LI') return;
    const own: Node[] = [];
    const nested: Element[] = [];
    li.childNodes.forEach((n) => {
      if (n.nodeType === Node.ELEMENT_NODE && ((n as Element).tagName === 'UL' || (n as Element).tagName === 'OL')) nested.push(n as Element);
      else own.push(n);
    });
    const runs = collectRuns(own);
    if (runs.some((r) => r.text.trim() !== '')) items.push({ runs, depth });
    nested.forEach((n) => collectListItems(n, depth + 1, items));
  });
}

function tableBlock(table: Element): Extract<SectionBlock, { type: 'table' }> {
  const rows: { cells: TableCell[] }[] = [];
  const spanAttr = (c: Element, name: string): number => {
    const n = parseInt(c.getAttribute(name) ?? '1', 10);
    return Number.isFinite(n) && n > 0 ? Math.min(20, n) : 1;
  };
  // Grid placement: `pending[col]` = rows still covered by a rowspan from above.
  // A covered slot gets a `spanned` placeholder so columns stay aligned.
  const pending: number[] = [];
  Array.from(table.querySelectorAll('tr')).forEach((tr) => {
    // Only rows of THIS table (no nested-table rows).
    if (tr.closest('table') !== table) return;
    const cells: TableCell[] = [];
    let col = 0;
    const fillCovered = () => {
      while ((pending[col] ?? 0) > 0) {
        pending[col]--;
        cells.push({ runs: [], header: false, colSpan: 1, rowSpan: 1, spanned: true });
        col++;
      }
    };
    Array.from(tr.children).forEach((c) => {
      if (c.tagName !== 'TD' && c.tagName !== 'TH') return;
      fillCovered();
      const colSpan = spanAttr(c, 'colspan');
      const rowSpan = spanAttr(c, 'rowspan');
      const header = c.tagName === 'TH';
      const swatch = header ? colourSwatchOf(textOf(c)) : undefined;
      cells.push({ runs: collectRuns(c.childNodes), header, colSpan, rowSpan, ...(swatch ? { swatch } : {}) });
      if (rowSpan > 1) for (let k = col; k < col + colSpan; k++) pending[k] = rowSpan - 1;
      col += colSpan;
    });
    fillCovered(); // trailing covered slots after the row's last authored cell
    if (cells.some((c) => !c.spanned)) rows.push({ cells });
  });
  // Placeholders inherit the header flag of their row so an all-<th> row stays one.
  rows.forEach((r) => {
    const head = r.cells.every((c) => c.spanned || c.header);
    r.cells.forEach((c) => { if (c.spanned) c.header = head; });
  });
  const hasHeaderRow = rows.length > 0 && rows[0].cells.every((c) => c.header);
  return { type: 'table', hasHeaderRow, rows };
}

function imageBlock(img: Element, caption: InlineRun[] | undefined): ImageBlock {
  const src = img.getAttribute('src') ?? '';
  const alt = img.getAttribute('alt') ?? '';
  const w = (img.getAttribute('width') ?? '').trim();
  let hint = 1;
  if (/^\d+%$/.test(w)) hint = parseInt(w, 10) / 100;
  else if (/^\d+$/.test(w)) hint = parseInt(w, 10) / EDITOR_COLUMN_PX;
  hint = Math.max(0.2, Math.min(1, hint || 1));
  const cap = caption && caption.some((r) => r.text.trim() !== '') ? caption : undefined;
  return { type: 'image', src, alt, caption: cap, widthHint: hint };
}

function alignOf(el: Element): 'left' | 'center' | 'right' {
  const a = (el.getAttribute('align') ?? '').toLowerCase();
  return a === 'center' || a === 'right' ? a : 'left';
}

// ── Inline runs ──────────────────────────────────────────────────────────────

function collectRuns(nodes: NodeList | Node[]): InlineRun[] {
  const runs: InlineRun[] = [];
  const list = Array.from(nodes as ArrayLike<Node>);
  list.forEach((n) => collectInline(n, {}, runs));
  return normalizeRuns(runs);
}

function collectInline(node: Node, style: { bold?: boolean; italic?: boolean; href?: string }, runs: InlineRun[]): void {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = (node.textContent ?? '').replace(/ /g, ' ').replace(/[ \t\r\n]+/g, ' ');
    if (text) runs.push({ text, ...style });
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const el = node as Element;
  switch (el.tagName) {
    case 'BR': runs.push({ text: '', br: true }); return;
    case 'STRONG': case 'B': el.childNodes.forEach((c) => collectInline(c, { ...style, bold: true }, runs)); return;
    case 'EM': case 'I': el.childNodes.forEach((c) => collectInline(c, { ...style, italic: true }, runs)); return;
    case 'A': {
      const href = (el.getAttribute('href') ?? '').trim();
      el.childNodes.forEach((c) => collectInline(c, { ...style, href: href || style.href }, runs));
      return;
    }
    case 'IMG': return; // inline images inside text are not drawn in the PDF
    default: el.childNodes.forEach((c) => collectInline(c, style, runs));
  }
}

/** Merge adjacent runs with identical styling; trim leading/trailing whitespace. */
function normalizeRuns(runs: InlineRun[]): InlineRun[] {
  const out: InlineRun[] = [];
  runs.forEach((r) => {
    const last = out[out.length - 1];
    if (last && !last.br && !r.br && !!last.bold === !!r.bold && !!last.italic === !!r.italic && (last.href ?? '') === (r.href ?? '')) {
      last.text += last.text.endsWith(' ') ? r.text.replace(/^ +/, '') : r.text;
    } else out.push({ ...r });
  });
  // Collapse whitespace across run boundaries and trim the ends.
  for (let i = 1; i < out.length; i++) {
    if (out[i - 1].text.endsWith(' ') && out[i].text.startsWith(' ')) out[i].text = out[i].text.replace(/^ +/, '');
  }
  if (out.length) { out[0].text = out[0].text.replace(/^ +/, ''); out[out.length - 1].text = out[out.length - 1].text.replace(/ +$/, ''); }
  return out.filter((r) => r.br || r.text !== '');
}

function textOf(el: Element): string {
  return (el.textContent ?? '').replace(/ /g, ' ');
}

function isMeaningful(b: SectionBlock): boolean {
  switch (b.type) {
    case 'heading': case 'paragraph': return b.runs.some((r) => r.text.trim() !== '');
    case 'image': return b.src !== '';
    default: return true;
  }
}

/** Consecutive standalone images flow into rows of up to MAX_IMAGES_PER_ROW. */
function groupImages(blocks: SectionBlock[]): SectionBlock[] {
  const out: SectionBlock[] = [];
  let run: ImageBlock[] = [];
  const flush = () => {
    for (let i = 0; i < run.length; i += MAX_IMAGES_PER_ROW) {
      const chunk = run.slice(i, i + MAX_IMAGES_PER_ROW);
      out.push(chunk.length === 1 ? chunk[0] : { type: 'imageRow', images: chunk });
    }
    run = [];
  };
  blocks.forEach((b) => {
    if (b.type === 'image') run.push(b);
    else { flush(); out.push(b); }
  });
  flush();
  return out;
}

/** Plain text of a run list (for measuring / fallbacks). */
export function runsToText(runs: InlineRun[]): string {
  return runs.map((r) => (r.br ? '\n' : r.text)).join('');
}
